<template>
  <transition name="modal-fade">
    <div v-if="match && !rochambo.minimized" class="rochambo-backdrop" />
  </transition>

  <transition name="modal-fade">
    <div v-if="match" class="rochambo" :class="{ closed: rochambo.minimized }" role="dialog"
      aria-labelledby="rochamboTitle">
      <h3 id="rochamboTitle">
        <span :class="{ title_notif: needsAttention && rochambo.minimized }">{{ t('modal.rochambo.title') }}</span>
        <font-awesome-icon :icon="rochambo.minimized ? 'plus-circle' : 'times-circle'" class="fa fa-times-circle" @click.stop="rochambo.toggle()" />
      </h3>

      <div class="content">
        <div class="duel">
          <div v-for="duelist in duelists" :key="duelist.slot" class="duelist" :class="{ winner: duelist.isWinner }">
            <span class="name">{{ duelist.name }}</span>
            <div class="pick">
              <font-awesome-icon :icon="duelist.icon" />
            </div>
            <span class="status">{{ duelist.status }}</span>
          </div>
          <span class="versus">{{ t('modal.rochambo.versus') }}</span>
        </div>

        <p v-if="match.revealed" class="result">
          <template v-if="rochambo.winner === -1">{{ t('modal.rochambo.draw') }}</template>
          <template v-else-if="winnerName">
            <em>{{ winnerName }}</em> {{ t('modal.rochambo.wins') }}
          </template>
        </p>

        <template v-else-if="isDuelist">
          <p>
            {{ hasSubmitted ? t('modal.rochambo.waitingReveal') : t('modal.rochambo.instructions') }}
          </p>
          <div class="button-group picker">
            <button v-for="choice in ROCHAMBO_PICKS" :key="choice" :class="{ chosen: rochambo.ownPick === choice }"
              @click="ownPickChoice(choice)">
              <font-awesome-icon :icon="PICK_ICONS[choice]" />
              <span>{{ pickLabel(choice) }}</span>
            </button>
          </div>
        </template>

        <p v-else class="hint">{{ t('modal.rochambo.ongoing') }}</p>

        <div v-if="isStoryteller" class="button-group">
          <button v-if="!match.revealed" class="townsfolk" :class="{ disabled: !canReveal }" :disabled="!canReveal"
            @click="rochambo.reveal()">
            {{ t('modal.rochambo.reveal') }}
          </button>
          <button class="demon" @click="rochambo.end()">
            {{ match.revealed ? t('modal.rochambo.close') : t('modal.rochambo.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  ROCHAMBO_PICKS,
  useLocaleStore,
  usePlayersStore,
  useRochamboStore,
  useSessionStore,
} from '@/stores';
import type { RochamboPick } from '@/types';

const locale = useLocaleStore();
const t = locale.t;
const playersStore = usePlayersStore();
const rochambo = useRochamboStore();
const session = useSessionStore();

const PICK_ICONS: Record<RochamboPick, string> = {
  rock: 'hand-back-fist',
  paper: 'hand-paper',
  scissors: 'hand-scissors',
};

const match = computed(() => rochambo.match);
const isStoryteller = computed(() => !session.isPlayerOrSpectator);

const ownSlot = computed(() => {
  const current = match.value;
  if (!current || !session.isPlayerOrSpectator || !session.playerId) return -1;
  return current.players.findIndex(
    (seat) => playersStore.players[seat]?.id === session.playerId,
  );
});
const isDuelist = computed(() => ownSlot.value > -1);
const hasSubmitted = computed(() => {
  const current = match.value;
  return !!current && ownSlot.value > -1 && !!current.submitted[ownSlot.value];
});

const canReveal = computed(
  () => !!match.value && match.value.picks.every((pick) => !!pick),
);

const needsAttention = computed(() => {
  const current = match.value;
  if (!current) return false;
  if (current.revealed) return true;
  if (isDuelist.value) return !hasSubmitted.value;
  return isStoryteller.value && canReveal.value;
});

const winnerName = computed(() => {
  const winner = rochambo.winner;
  if (winner === null || winner === -1) return '';
  return playersStore.players[winner]?.name || '';
});

const duelists = computed(() => {
  const current = match.value;
  if (!current) return [];
  return current.players.map((seat, slot) => {
    // Picks stay secret until the reveal: you only ever see your own, the storyteller sees both
    const pick = current.picks[slot] ?? null;
    const isVisiblePick = current.revealed || isStoryteller.value;
    const shownPick = isVisiblePick
      ? pick
      : slot === ownSlot.value
        ? rochambo.ownPick
        : null;
    return {
      seat,
      slot,
      pick,
      name: playersStore.players[seat]?.name || '',
      icon: shownPick ? PICK_ICONS[shownPick] : 'question',
      status: status(slot, current.revealed, pick, !!current.submitted[slot]),
      isWinner: current.revealed && rochambo.winner === seat,
    };
  });
});

function pickLabel(pick: RochamboPick) {
  return t(`modal.rochambo.picks.${pick}`);
}

function status(
  slot: number,
  revealed: boolean,
  pick: RochamboPick | null,
  submitted: boolean,
) {
  if (revealed) return pick ? pickLabel(pick) : '';
  if (!submitted) return t('modal.rochambo.waitingPick');
  if (slot === ownSlot.value) return t('modal.rochambo.sent');
  return t('modal.rochambo.picked');
}

function ownPickChoice(pick: RochamboPick) {
  const current = match.value;
  const seat = current?.players[ownSlot.value];
  if (seat === undefined) return;
  rochambo.pick(seat, pick);
}
</script>

<style scoped lang="scss">
.rochambo-backdrop {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 200;
}

.rochambo {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 201;
  background: var(--background-color);
  backdrop-filter: blur(10px);
  border: 3px solid var(--border-color);
  border-radius: 10px;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.5));
  padding: 10px 20px;
  max-width: 90vw;
  max-height: 95vh;
  overflow-y: auto;
  text-align: center;
  transition: all 250ms ease-in-out;

  h3 {
    display: flex;
    align-items: center;
    align-content: center;
    justify-content: center;
    margin: 5px 0;

    span {
      flex-grow: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &.title_notif {
        color: red;
      }
    }

    svg {
      cursor: pointer;
      flex-grow: 0;

      &.fa-minus-circle {
        margin-left: 0.25rem;
        margin-right: 0.5rem;
      }

      &.fa-plus-circle {
        margin-left: 0.25rem;
        margin-right: 0.5rem;
        display: none;
      }
    }
  }

  .content {
    transition: all 250ms ease-in-out;
  }

  // Collapsed to its title bar at the top of the screen: the duel is never dismissed, only folded away
  &.closed {
    top: 10px;
    transform: translate(-50%, 0);
    overflow: hidden;

    svg.fa-minus-circle {
      display: none;
    }

    svg.fa-plus-circle {
      display: block;
    }

    .content,
    .content * {
      width: 0;
      height: 0;
      margin: 0;
      scale: 0;
    }
  }
}

.duel {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 4em;
  margin: 20px 0 10px;

  .versus {
    position: absolute;
    top: 45%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-family: PiratesBay, sans-serif;
    font-size: 1.4em;
    opacity: 0.8;
  }
}

.duelist {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 8em;

  .name {
    font-family: PiratesBay, sans-serif;
    font-size: 1.2em;
    max-width: 10em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .pick {
    font-size: 3em;
    margin: 10px 0 5px;
    filter: drop-shadow(0 0 3px black);
  }

  .status {
    font-size: 0.9em;
    opacity: 0.8;
    min-height: 1.4em;
  }

  &.winner {
    .name {
      color: var(--townsfolk);
    }

    .pick {
      filter: drop-shadow(0 0 8px var(--townsfolk));
    }
  }

  .button-group button {
    &.chosen {
      color: var(--townsfolk);
    }
  }
}

.result {
  font-family: PiratesBay, sans-serif;
  font-size: 1.3em;

  em {
    color: var(--townsfolk);
    font-style: normal;
  }
}

.hint {
  font-size: 0.9em;
  opacity: 0.7;
}

.button-group.picker button {
  font-size: 1.6em;

  span {
    font-size: 0.6em;
  }

  &.chosen {
    color: var(--townsfolk);
    text-shadow: 0 0 5px var(--townsfolk);
  }
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
</style>
